import React, { useEffect, useState, useRef } from 'react';
import useTrackEvent from '../../utils/trackEvent';
import { CSSTransition } from 'react-transition-group';
import type { Question, Answer } from '../../types/questionnaire';
import { Styling } from '@/types/customer';

interface Props {
  themes: Question;
  onChecked: (id: number) => void;
  selectedThemes: Answer[];
  wrappedThemes: Answer[];
  setWrappedThemes: React.Dispatch<React.SetStateAction<Answer[]>>;
  submitThemes: () => void;
}

const Themes = ({ themes, onChecked, selectedThemes, wrappedThemes, setWrappedThemes, submitThemes }: Props) => {
  const trackEvent = useTrackEvent();
  const [isDisabled, setIsDisabled] = useState(true);
  const [lineCoords, setLineCoords] = useState<{ y1: number | null; y2: number | null; height: number | null }>({
    y1: null, y2: null, height: null,
  });
  const nodeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const selected = themes.answers.some((theme) => theme.checked);
    setIsDisabled(!selected);
  }, [themes]);

  useEffect(() => {
    const currentSection = document.getElementById('themesSection');
    const currentSectionLabel = document.getElementById('themesSectionLabel');
    const marginTop = 24;
    const gapBtwSections = 24;

    if (!currentSection || !currentSectionLabel) return;

    const handleResize = () => {
      setLineCoords({
        y1: currentSectionLabel.clientHeight / 2 + marginTop,
        y2: currentSection.clientHeight + gapBtwSections + marginTop + currentSectionLabel.clientHeight / 2,
        height: currentSection.clientHeight + gapBtwSections + marginTop + currentSectionLabel.clientHeight / 2,
      });
    };

    if (!selectedThemes.length) return;

    const observer = new ResizeObserver(handleResize);
    observer.observe(currentSection);

    return () => { observer.disconnect(); };
  }, [selectedThemes]);

  const toggleSelectTheme = (id: number) => {
    onChecked(id);
    const updatedwrappedThemes = wrappedThemes.filter((theme) => {
      if (!theme.checked) return false;
      return selectedThemes.some((selectedTheme) => selectedTheme.id === theme.id);
    });
    setWrappedThemes(updatedwrappedThemes);
  };

  return (
    <div id="themesSection" className="relative flex flex-col w-full gap-5 lg:flex-row lg:gap-6">
      <div id="themesSectionLabel" className="flex-none h-fit w-full lg:max-w-[171px] flex flex-col lg:flex-row lg:gap-3 items-center lg:mt-6">
        <span className={`w-full flex-1 bg-white rounded border-2 py-2 px-3 font-bold text-center lg:text-left ${selectedThemes.length && !isDisabled ? 'border-secondary-green' : 'border-secondary-orange'}`}>
          Thema's
        </span>
        <div className={`lg:hidden arrow-down border-t-[16px] ${selectedThemes.length && !isDisabled ? 'border-secondary-green' : 'border-secondary-orange'}`}></div>
        {selectedThemes.length && !isDisabled ? (
          <span className="hidden z-10 lg:block w-5 h-5 bg-secondary-green rounded-full p-1">
            <img src="/assets/images/tick.svg" className="w-full" alt="tick" />
          </span>
        ) : (
          <span className="hidden z-10 lg:block h-5 w-5 bg-secondary-orange rounded-full"></span>
        )}
      </div>
      <div className="flex-1 lg:inline-flex">
        <div className="hidden lg:block arrow-left lg:mt-6"></div>
        <div className="flex-1 rounded-lg p-5 bg-white lg:rounded-[20px] lg:p-10">
          <h4>{themes.title}</h4>
          <div className="mt-3 lg:grid space-y-3 lg:space-y-0 lg:gap-6 lg:grid-cols-4">
            {themes.answers.map((theme) => (
              <label
                className={`flex-1 flex flex-row lg:flex-col p-3 gap-3 lg:pb-6 bg-main-light items-center cursor-pointer border-2 rounded-[6px] sm:min-h-[180px] ${theme.checked ? 'border-[' + Styling.PrimaryColor() + ']' : 'border-transparent'} hover:border-[` + Styling.BorderHover() + `]`}
                key={theme.id}
              >
                <input
                  type="checkbox"
                  className="lg:self-end cursor-pointer"
                  checked={theme.checked}
                  onChange={() => toggleSelectTheme(theme.id)}
                  onClick={() => trackEvent('Select_Theme', { select: theme.title })}
                />
                <img src={'/assets/images/' + theme.image + '.svg'} alt={theme.title + ' icon'} className="w-8 h-8 lg:w-10 lg:h-10 lg:self-center" />
                <h5 className="lg:text-center lg:self-center text-lg font-bold">{theme.title}</h5>
              </label>
            ))}
          </div>
          <button className="btn btn-primary w-full sm:w-fit mt-3 lg:mt-5" onClick={submitThemes} disabled={isDisabled}>
            Volgende
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      {!!selectedThemes.length && lineCoords.y1 && lineCoords.y2 && lineCoords.height && (
        <CSSTransition nodeRef={nodeRef} in timeout={300} classNames="fade" unmountOnExit appear={true}>
          <svg ref={nodeRef as unknown as React.RefObject<SVGSVGElement>} width="162" height={lineCoords.height} className="hidden lg:block absolute">
            <line x1="162" y1={lineCoords.y1} x2="162" y2={lineCoords.y2} strokeWidth="4px" stroke="#95CF5B" strokeLinecap="round" strokeDasharray="10,10" />
          </svg>
        </CSSTransition>
      )}
    </div>
  );
};

export default Themes;
